import React from 'react'

/**
 * props:
 * - rows: Array<{ name, rel_path, size_bytes, year, labels?: string[] | string, public_url?: string }>
 * - selected: Set<string> (keys by rel_path)
 * - onToggleRow(rel_path)
 * - pageSize: number
 * - onPageSizeChange(n)
 * - disabled: boolean
 */
export default function ResultsTable({
  rows = [],
  selected = new Set(),
  onToggleRow,
  pageSize = 100,
  onPageSizeChange,
  disabled,
}) {
  const sizes = [50, 100, 200]

  return (
    <div className={`rounded border bg-white ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="border-b px-3 py-2 text-sm font-medium flex items-center justify-between">
        <div>Results</div>
        <div className="flex items-center gap-2 text-xs">
          <span>Page size</span>
          <select
            className="border rounded px-2 py-1"
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
            disabled={disabled}
          >
            {sizes.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="w-10 px-3 py-2 text-left">Sel</th>
              <th className="px-3 py-2 text-left">file_name</th>
              <th className="px-3 py-2 text-left">rel_path</th>
              <th className="px-3 py-2 text-left">file_size</th>
              <th className="px-3 py-2 text-left">year</th>
              <th className="px-3 py-2 text-left">labels</th>
              <th className="px-3 py-2 text-left">link</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">No results.</td>
              </tr>
            ) : (
              rows.map((r) => {
                const key = r.rel_path || r.name
                const checked = selected.has(key)
                const labels =
                  Array.isArray(r.labels) ? r.labels.join(', ') :
                  (typeof r.labels === 'string' ? r.labels : '')
                const size = formatBytes(r.size_bytes)

                return (
                  <tr key={key} className="border-t">
                    <td className="px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={checked}
                        onChange={() => onToggleRow?.(key)}
                        disabled={disabled}
                      />
                    </td>
                    <td className="px-3 py-2 align-top break-words">{r.name}</td>
                    <td className="px-3 py-2 align-top break-words">
                      <span title={r.rel_path}>{r.rel_path}</span>
                    </td>
                    <td className="px-3 py-2 align-top">{size}</td>
                    <td className="px-3 py-2 align-top">{r.year ?? ''}</td>
                    <td className="px-3 py-2 align-top">{labels}</td>
                    <td className="px-3 py-2 align-top">
                      {r.public_url ? (
                        <a href={r.public_url} target="_blank" rel="noreferrer" className="underline">
                          open
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function formatBytes(n) {
  if (n == null || isNaN(n)) return ''
  const units = ['B','KB','MB','GB','TB']
  let i = 0
  let v = Number(n)
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`
}
